import * as ko from 'knockout';
import type { IInjector } from '@paperbits/common/injection/IInjector';
import type TTQuery from '../../../../cms/client/TTQuery';
import type { ICmsContentPermalinkResolver } from '../../../../cms/content/ICmsContentPermalinkResolver';
import type { CmsContentContract, CmsContentsListContract } from '../../../../cms/content/ko/CmsContentContract';
import type { ICustomPermalinkResolver } from '../../../../media/ICustomPermalinkResolver';
import type { CustomRouter } from '../../../../routing/customRouter';
import { CardsWallEndpointType, type QueryInput, makeQuery, overrideQueryInput } from '../../../cards-wall/cardsWallUtils';
import { FilterParameters } from '../../../cards-wall/ko/runtime/cardsWallFilterParameters';
import { ReplyCardViewModel, type ReplyCardViewModelInjectables } from '../../../reply-card/ko/replyCardViewModel';
import { ReplyCardModel } from '../../../reply-card/replyCardModel';
import { type CollectionSliderRuntimeModel, CollectionSliderRuntimeModelBuilder } from './collectionSliderRuntimeModel';
import { CollectionSliderRuntimeViewModel } from './collectionSliderRuntimeViewModel';
import { BundledSettingsProvider } from '../../../../configuration/bundledSettingsProvider';
import { CareersEventApiClient, type TTCareersQuery } from '../../../../api/CareersEventApiClient';
import type { SearchApiConfig } from '../../../cards-wall/ko/runtime/cards-wall-runtime';
import { SliderHTMLElement } from '../../../../common/slider/slider';
import { type CarouselTypeOption, CarouselTypeOptions } from '../../../card-carousel/ko/carouselTypeOptions';
import type { ICMSRuntimeClient } from '../../../../cms/client/ICMSRuntimeClient';

const ATTRIBUTES = [
    'data-slider-type',
    'data-infinite',
    'data-init-infinite-on-load',
    'data-items-count',
    'data-theme',
    'data-carousel-autoplay',
    'data-carousel-autoplay-interval',
    'data-carousel-pauseonhover',
    'data-edit-mode',
    'params'
];

export class CollectionSliderHTMLElement extends SliderHTMLElement {
    private wrapperElements: () => NodeListOf<HTMLElement>;
    private runtimeViewModel: CollectionSliderRuntimeViewModel;
    private runtimeModel: CollectionSliderRuntimeModel;
    private injected: ReplyCardViewModelInjectables;
    private apiClient: ICMSRuntimeClient;
    private injectionDependenciesResolved: boolean = false;
    private router: CustomRouter;

    private carouselTypeOption: CarouselTypeOption;

    constructor() {
        super({
            containerSelector: '.carousel-items-container',
            currentSlideIndex: 1,
            indicatorSelector: '.carousel-indicator',
            itemsContainerSelector: '.carousel-items',
            itemSelector: '.carousel-item',
            nextNavSelector: '.carousel-control-next',
            prevNavSelector: '.carousel-control-prev',
        });
        this.initInfiniteOnLoad = false;
        this.startingSlideIndex = 1;

        this.wrapperElements = () => this.querySelectorAll<HTMLElement>('.carousel-items--runtime');
    }

    static get observedAttributes(): string[] {
        return ATTRIBUTES;
    }

    public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (!ATTRIBUTES.includes(name)) {
            return;
        }

        if (oldValue === newValue) {
            return;
        }

        super.attributeChangedCallback(name, oldValue, newValue);

        setTimeout(() => {
            switch (name) {
                case 'data-slider-type': {
                    const typeReset = this.carouselTypeOption !== undefined;
                    this.carouselTypeOption = CarouselTypeOptions.find((option: CarouselTypeOption) => option.value === newValue);
                    this.currentSlideCentered = this.carouselTypeOption.centered;
                    this.fullWSlide = this.carouselTypeOption.fullW;
                    this.sliderType = `CollectionSlider - ${this.carouselTypeOption.label}`;
                    this.reset(typeReset ? this.startingSlideIndex : undefined);
                    break;
                }
                case 'data-infinite':
                    this.infinite = newValue === 'true';
                    oldValue === 'true' && this.infiniteConfigured && this.cleanUpInfinite();
                    newValue === 'true' && this.initInfiniteOnLoad && this.setUpInfinite();
                    break;
                case 'data-init-infinite-on-load':
                    this.initInfiniteOnLoad = newValue === 'true';
                    oldValue === 'true' && this.infiniteConfigured && this.cleanUpInfinite();
                    newValue === 'true' && this.infinite && this.setUpInfinite();
                    break;
                case 'data-items-count':
                    this.itemsTotal = Number.parseInt(newValue);
                    this.reset(this.startingSlideIndex);
                    break;
                case 'data-carousel-autoplay-interval':
                    this.autoplayInterval = Number.parseInt(newValue);
                    if (this.autoplay) {
                        this.disableAutoplay();
                        this.enableAutoplay();
                    }
                    break;
                case 'data-carousel-pauseonhover':
                    this.autoplayPauseOnHover = newValue === 'true';
                    this.autoplayPauseOnHover ? this.enablePauseOnHover() : this.disablePauseOnHover();
                    break;
                case 'data-carousel-autoplay':
                    this.autoplay = newValue === 'true';
                    this.autoplay ? this.enableAutoplay() : this.disableAutoplay();
                    break;
                case 'data-theme':
                    if (oldValue !== null) {
                        this.reset();
                    }
                    break;
                case 'params':
                    try {
                        this.runtimeModel = CollectionSliderRuntimeModelBuilder.fromSerialized(newValue, true);
                    } catch {
                        console.error('Unable to parse "params" for "collection-slider".');
                    }
                    if (this.runtimeModel) {
                        if (this.runtimeModel.model?.debug) {
                            console.log(`[Collection Slider] Deserialized model from "params" attribute: `, this.runtimeModel);
                        }
                        this.overrideRuntimeModelAndApplyBindings();
                    }
                    break;
                default:
                    break;
            }
        });
    }

    private async applyBindings(): Promise<void> {
        if (!this.runtimeViewModel) {
            this.runtimeViewModel = new CollectionSliderRuntimeViewModel(this.runtimeModel);
        }
        if (this.runtimeViewModel) {
            this.runtimeViewModel.preloadedItems(this.runtimeModel.preloadedItems);
            if (!this.runtimeModel.model.staticItems) {
                const contentsList: CmsContentsListContract = await this.getCards(this.runtimeModel.queryInput);
                const cardsViewModels: ReplyCardViewModel[] = await this.contentContractsToViewModels(contentsList.list);
                this.runtimeViewModel.cards([...cardsViewModels]);
            } else {
                this.runtimeViewModel.cards([]);
            }
        }
        for (const w of this.wrapperElements()) {
            if (w !== null) {
                if (!ko.contextFor(w)) {
                    ko.applyBindings(this.runtimeViewModel, w);
                }
            }
        }
    }

    private async contentContractsToViewModels(contracts: CmsContentContract[]): Promise<ReplyCardViewModel[]> {
        const viewModels: Promise<ReplyCardViewModel>[] = [];
        for (const contract of contracts) {
            const m = await ReplyCardModel.contentContractToModel(
                contract,
                this.injected,
                this.runtimeModel.siteKey,
                true,
                this.runtimeViewModel.endpointType() === CardsWallEndpointType.DEFAULT
            );
            viewModels.push(ReplyCardViewModel.modelToViewModel(m, this.injected, null, { site: { key: this.runtimeModel.siteKey } }));
        }
        return await Promise.all(viewModels);
    }

    private async getCards(queryInput: QueryInput): Promise<CmsContentsListContract> {
        const emptyResult: CmsContentsListContract = { list: [], paginationInfo: { limit: queryInput.limit, nextOffset: 0, startOffset: queryInput.offset, total: 0 } };
        const endpointType = this.runtimeViewModel.endpointType.peek();
        if (!this.apiClient) {
            if (endpointType === CardsWallEndpointType.CAREERS_EVENT) {
                const apiConfig = BundledSettingsProvider.newInstance().getSettingSync<SearchApiConfig>('search');
                this.apiClient = CareersEventApiClient.build({ endpoint: apiConfig.endpoint });
            } else {
                const globalInjector: IInjector = await window['globalInjector'];
                this.apiClient = globalInjector.resolve<ICMSRuntimeClient>('apiClient');
            }
        }
        const query: TTQuery = makeQuery(queryInput);
        if (endpointType === CardsWallEndpointType.CAREERS_EVENT) {
            (<TTCareersQuery>query).fromStartDate = true;
        }
        let result: { data: CmsContentsListContract };
        try {
            result = await this.apiClient.listContents(query);
        } catch {
            console.error('[Collection Slider] It was not possible to retrieve contents.', this);
        }
        if (result?.data) {
            this.setAttribute('data-items-count', result.data.list.length.toString());
            return <CmsContentsListContract>result.data;
        } else {
            this.setAttribute('data-items-count', '0');
            return emptyResult;
        }
    }

    private async overrideRuntimeModelAndApplyBindings(): Promise<void> {
        if (!this.injectionDependenciesResolved) {
            await this.resolveInjectionDependencies();
            this.injectionDependenciesResolved = true;
        }
        if (this.router && this.runtimeModel?.model) {
            const params: [string, string][] = this.router.getSearchParams();
            overrideQueryInput(this.runtimeModel.queryInput, new FilterParameters(params));
        }
        this.applyBindings();
    }

    private async resolveInjectionDependencies(): Promise<void> {
        const globalInjector: IInjector = await window['globalInjector'];
        this.injected = {
            mediaPermalinkResolver: globalInjector.resolve<ICustomPermalinkResolver>('mediaPermalinkResolver'),
            cmsContentPermalinkResolver: globalInjector.resolve<ICmsContentPermalinkResolver>('cmsContentPermalinkResolver')
        };
    }

    public disconnectedCallback(): void {
        for (const w of this.wrapperElements()) {
            if (w !== null) {
                if (!ko.contextFor(w)) {
                    ko.cleanNode(w);
                }
            }
        }
    }
}
